import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { applyThemeVars, buildCloseButton, buildForm, buildHeader, createHost, resolvePopupStyle, SHARED_PANEL_STYLE, type LeadFormTheme, type RenderHandle } from './shared';

/** A persistent, always-visible corner tab — its own trigger only controls when the tab itself
 * appears (e.g. after a scroll or delay), not when the form opens; the visitor opens the form by
 * clicking the tab, same as OptinMonster's widget campaigns. */
const STYLE = `
  ${SHARED_PANEL_STYLE}
  .tab {
    position: fixed; bottom: 20px; z-index: 2147483000; border: none; cursor: pointer;
    padding: 12px 18px; border-radius: 999px; font-size: 14px; font-weight: 600;
    background: var(--cms-lf-button-bg); color: var(--cms-lf-button-text);
    box-shadow: 0 8px 24px rgba(0,0,0,0.25);
  }
  .tab.bottom-right { right: 20px; }
  .tab.bottom-left { left: 20px; }
  .panel {
    position: fixed; bottom: 76px; z-index: 2147483000; width: min(360px, calc(100vw - 40px));
    display: none;
  }
  .panel.bottom-right { right: 20px; }
  .panel.bottom-left { left: 20px; }
  .panel.open { display: block; }
`;

export function renderWidget(
  form: LeadFormPublicConfig,
  client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const { host, root } = createHost(STYLE);
  const corner = theme.corner ?? 'bottom-right';

  const tab = document.createElement('button');
  tab.type = 'button';
  tab.className = `tab ${corner}`;
  tab.textContent = theme.widgetLabel || theme.ctaLabel || 'Get updates';
  applyThemeVars(tab, resolved);

  const panel = document.createElement('div');
  panel.className = `panel ${corner}`;
  applyThemeVars(panel, resolved);

  panel.appendChild(buildCloseButton(() => panel.classList.remove('open')));
  for (const el of buildHeader(form, client, panel)) {
    panel.appendChild(el);
  }
  panel.appendChild(buildForm(form.fields, theme.ctaLabel || 'Submit', onSubmit, close));

  tab.addEventListener('click', () => panel.classList.toggle('open'));

  root.appendChild(tab);
  root.appendChild(panel);

  function close() {
    host.remove();
  }

  return { close };
}
